const STORAGE_KEY = 'tc_transport'
const DIRECT_TIMEOUT_MS = 1500
const SWITCH_HOLD_MS = 10 * 60 * 1000
const DEFAULT_PROXY_SEARCH_PATH = '/apps/tc/search'
const DIRECT_DEBOUNCE_MS = 140
const PROXY_DEBOUNCE_MS = 250
const RATE_LIMIT_BACKOFF_MS = 500

// `mode` is `'proxy'` (the Shopify App Proxy in front of Mithril) or `'direct'`
// (Meilisearch itself). `''` means "follow the configured read path".
const DEFAULT_STATE = { mode: '', until: 0, indexFallback: false, localeFallbackUntil: 0 }

function readConfig(options) {
  if (typeof options.config === 'function') return options.config() || {}
  return options.config || {}
}

function memoryFallbackStorage() {
  const store = new Map()
  return {
    getItem: (key) => store.get(key) ?? null,
    setItem: (key, value) => {
      store.set(key, value)
    },
    removeItem: (key) => {
      store.delete(key)
    },
  }
}

function defaultStorage() {
  try {
    const storage = globalThis.sessionStorage
    storage.getItem(STORAGE_KEY)
    return storage
  } catch {
    return memoryFallbackStorage()
  }
}

function loadState(storage) {
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_STATE }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_STATE }
    return {
      mode: parsed.mode === 'proxy' || parsed.mode === 'direct' ? parsed.mode : '',
      until: typeof parsed.until === 'number' ? parsed.until : 0,
      indexFallback: parsed.indexFallback === true,
      localeFallbackUntil:
        typeof parsed.localeFallbackUntil === 'number' ? parsed.localeFallbackUntil : 0,
    }
  } catch {
    // ignore corrupt storage
  }
  return { ...DEFAULT_STATE }
}

function saveState(storage, state) {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // private mode / quota
  }
}

function listParam(value) {
  if (value == null) return ''
  return Array.isArray(value) ? value.join(',') : String(value)
}

function proxySearchUrl(params, locale, path) {
  const base = typeof path === 'string' && path.trim() ? path.trim() : DEFAULT_PROXY_SEARCH_PATH
  const qs = new URLSearchParams()
  if (params.q != null) qs.set('q', params.q)
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.offset != null) qs.set('offset', String(params.offset))
  if (params.distinct) qs.set('distinct', String(params.distinct))
  const attributes = listParam(params.attributesToRetrieve)
  if (attributes) qs.set('attributesToRetrieve', attributes)
  const highlight = listParam(params.attributesToHighlight)
  if (highlight) qs.set('attributesToHighlight', highlight)
  if (params.highlightPreTag) qs.set('highlightPreTag', params.highlightPreTag)
  if (params.highlightPostTag) qs.set('highlightPostTag', params.highlightPostTag)
  if (locale) qs.set('locale', locale)
  const query = qs.toString()
  return query ? `${base}?${query}` : base
}

function httpError(status, body) {
  const error = new Error(`Meilisearch: ${status}`)
  error.status = status
  error.body = body
  return error
}

async function readJson(response) {
  const data = await response.json()
  if (!response.ok) throw httpError(response.status, data)
  return data
}

function isUserAbort(externalSignal) {
  return Boolean(externalSignal?.aborted)
}

/**
 * The shape callers test for (`error.name === 'AbortError'`). Every abort path
 * must produce it: a 429 that leaked out of a backoff wait or out of the catch
 * would be reported as a transport failure when the shopper merely kept typing.
 */
function abortError() {
  const error = new Error('Aborted')
  error.name = 'AbortError'
  return error
}

function isMissingIndex(error) {
  if (!error) return false
  if (error.body?.code === 'index_not_found') return true
  return error.status === 404
}

/** A rejected request: the caller's problem, not a transport one. */
function isCallerError(error) {
  return error?.status === 400
}

/**
 * A throttle. The proxy allows 240 req/min per IP, which fast typing reaches, so
 * this is "slow down" rather than "this path is broken".
 */
function isRateLimited(error) {
  return error?.status === 429
}

/**
 * Why a direct Meilisearch attempt should hand over to the app proxy, or `''`
 * when the error belongs to the caller (a bad query) and must surface.
 *
 * Mithril is the only Meilisearch reader (mithril#184): the instance has no
 * public host and no per-consumer keys, so a dead key (401/403), a removed
 * host or index (404), a non-JSON body (parked domain) and dead connections
 * all mean the browser can no longer reach Meilisearch by itself.
 */
function directFailureReason(error) {
  if (!error) return ''
  if (error.status === 401 || error.status === 403) return `auth-${error.status}`
  if (error.status === 404) return 'not-found'
  if (error.name === 'AbortError') return 'timeout'
  if (error.name === 'TypeError' || error.name === 'SyntaxError') return 'network'
  return ''
}

export function createSearchTransport(options = {}) {
  const storage = options.storage || defaultStorage()
  const now = options.now || Date.now
  const fetchImpl = options.fetch || globalThis.fetch.bind(globalThis)
  const log = options.log || ((message) => console.info(message))
  const directTimeoutMs = options.directTimeoutMs || DIRECT_TIMEOUT_MS
  const switchHoldMs = options.switchHoldMs || SWITCH_HOLD_MS
  const rateLimitBackoffMs = options.rateLimitBackoffMs ?? RATE_LIMIT_BACKOFF_MS
  let switched = false

  const current = () => loadState(storage)
  const activeConfig = () => readConfig(options)

  const persist = (patch) => {
    const state = { ...current(), ...patch }
    saveState(storage, state)
    return state
  }

  const directReady = () => Boolean(activeConfig().host && activeConfig().apiKey)

  function backoff(signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) return reject(abortError())
      const timer = setTimeout(resolve, rateLimitBackoffMs)
      signal?.addEventListener?.('abort', () => {
        clearTimeout(timer)
        reject(abortError())
      }, { once: true })
    })
  }

  /**
   * Which read path this storefront prefers. The App Proxy is the default
   * (mithril#184 made Mithril the only Meilisearch reader); direct Meilisearch
   * is only preferred when explicitly configured *and* credentialed.
   */
  function preferredPath() {
    return activeConfig().readPath === 'direct_meilisearch' && directReady() ? 'direct' : 'proxy'
  }

  /** The path in use right now: the preference, or a pinned switch still in force. */
  function activePath() {
    const state = current()
    const preferred = preferredPath()
    if (state.mode && state.mode !== preferred && now() < state.until) {
      if (state.mode === 'direct' && !directReady()) return preferred
      return state.mode
    }
    return preferred
  }

  const pinPath = (mode, reason) => {
    const state = persist({ mode, until: now() + switchHoldMs })
    if (!switched) {
      switched = true
      log(`[tc-search] read-path=${mode} reason=${reason}`)
    }
    return state
  }

  const clearPath = () => persist({ mode: '', until: 0 })

  const proxySearchPath = () => {
    const path = activeConfig().proxyPath
    return typeof path === 'string' && path.trim() ? path.trim() : DEFAULT_PROXY_SEARCH_PATH
  }

  const proxyHealthPath = () => proxySearchPath().replace(/[^/]*$/, 'health')

  /**
   * Picks the index for this request: the locale-specific one when the config
   * provides it, until a 404 proves the instance does not have it.
   */
  function resolveTarget() {
    const config = readConfig(options)
    const state = current()
    const baseIndex = config.index
    const localeIndex =
      config.localeIndex && config.localeIndex !== baseIndex ? config.localeIndex : ''
    const useLocaleIndex = Boolean(localeIndex) && !state.indexFallback

    return { config, baseIndex, index: useLocaleIndex ? localeIndex : baseIndex }
  }

  /**
   * The proxy resolves `locale` to a per-locale index itself and falls back to
   * the base index server-side, so it gets the locale whenever this storefront
   * locale differs from the base index — including after a direct-mode index
   * fallback, which says nothing about the proxy's own indexes.
   */
  function proxyLocale() {
    const config = activeConfig()
    const state = current()
    if (now() < state.localeFallbackUntil) return ''
    return config.localeIndex && config.localeIndex !== config.index && config.locale
      ? config.locale
      : ''
  }

  const fallbackEnabled = () => Boolean(readConfig(options).fallback)

  async function timed(url, init, timeoutMs) {
    const controller = new AbortController()
    const external = init?.signal
    const onAbort = () => controller.abort()
    if (external) {
      if (external.aborted) controller.abort()
      else external.addEventListener('abort', onAbort, { once: true })
    }
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await fetchImpl(url, { ...init, signal: controller.signal })
    } finally {
      clearTimeout(timer)
      if (external) external.removeEventListener('abort', onAbort)
    }
  }

  async function directFetch(params, signal, index) {
    const config = readConfig(options)
    const response = await timed(
      `${config.host}/indexes/${index || config.index}/search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.apiKey}`,
        },
        body: JSON.stringify(params),
        signal,
      },
      directTimeoutMs,
    )
    return readJson(response)
  }

  async function directSearch(params, signal) {
    const target = resolveTarget()

    try {
      return await directFetch(params, signal, target.index)
    } catch (error) {
      if (isUserAbort(signal)) throw error
      if (target.index !== target.baseIndex && isMissingIndex(error)) {
        persist({ indexFallback: true })
        log(`[tc-search] index=${target.index} missing, falling back to ${target.baseIndex}`)
        return directFetch(params, signal, target.baseIndex)
      }
      throw error
    }
  }

  async function proxyFetch(params, signal) {
    const locale = proxyLocale()
    const path = proxySearchPath()

    try {
      const response = await fetchImpl(proxySearchUrl(params, locale, path), {
        method: 'GET',
        signal,
      })
      return await readJson(response)
    } catch (error) {
      if (!locale || isUserAbort(signal) || error.status !== 400) throw error
      // The proxy resolves `locale` -> per-locale index and falls back to the
      // base index when that index is missing. While the Meilisearch search
      // key is not yet authorized for the storefront_<locale> indexes,
      // Meilisearch answers 400 (not 404) and that fallback cannot fire, so
      // the shopper keeps base-language results instead of none.
      persist({ localeFallbackUntil: now() + switchHoldMs })
      log('[tc-search] locale rejected by the proxy, retrying on the base index')
      const response = await fetchImpl(proxySearchUrl(params, '', path), {
        method: 'GET',
        signal,
      })
      return readJson(response)
    }
  }

  async function search(params, { signal } = {}) {
    const preferred = preferredPath()
    const active = activePath()

    // A successful request on the preferred path releases any pinned switch.
    const settle = () => {
      if (active !== preferred && current().mode === active) clearPath()
    }

    if (active === 'proxy') {
      try {
        const result = await proxyFetch(params, signal)
        settle()
        return result
      } catch (error) {
        // An abort outranks whatever error is in hand: callers key on `name`, so
        // rethrowing the 429 here would read as a broken reader, not a keystroke.
        if (isUserAbort(signal)) throw abortError()
        if (isRateLimited(error)) {
          // Wait out the throttle and retry the same path; pinning here would
          // desert a healthy proxy for the rest of the session. An abort during
          // the wait rejects with an AbortError, never the 429.
          await backoff(signal)
          if (isUserAbort(signal)) throw abortError()
          const result = await proxyFetch(params, signal)
          settle()
          return result
        }
        if (!fallbackEnabled() || !directReady() || isCallerError(error)) throw error
        pinPath('direct', 'proxy-failed')
        return directSearch(params, signal)
      }
    }

    try {
      const result = await directSearch(params, signal)
      settle()
      return result
    } catch (error) {
      if (isUserAbort(signal)) throw abortError()
      if (isCallerError(error)) throw error
      if (isRateLimited(error)) {
        await backoff(signal)
        if (isUserAbort(signal)) throw abortError()
        const result = await directSearch(params, signal)
        settle()
        return result
      }
      if (!fallbackEnabled()) throw error
      const transient = Boolean(error.status && error.status >= 500)
      const reason = transient ? '' : directFailureReason(error)
      if (!reason && !transient) throw error
      // A transient upstream failure is retried on the proxy once without
      // pinning the session to it.
      if (reason) pinPath('proxy', reason)
      return proxyFetch(params, signal)
    }
  }

  async function warmup({ signal } = {}) {
    const active = activePath()
    if (!fallbackEnabled()) return active

    const other = active === 'proxy' ? 'direct' : 'proxy'

    try {
      const url = active === 'proxy' ? proxyHealthPath() : `${activeConfig().host}/health`
      const response = await timed(url, { method: 'GET', signal }, directTimeoutMs)
      if (!response.ok && response.status >= 500) throw new Error(`${active} health ${response.status}`)
      return active
    } catch (error) {
      if (isUserAbort(signal)) throw error
      // Nothing to hand over to (or no credentials for direct): stay put.
      if (other === 'direct' && !directReady()) return active
      pinPath(other, `${active}-health`)
      return other
    }
  }

  const mode = () => activePath()

  function debounceMs() {
    return mode() === 'proxy' ? PROXY_DEBOUNCE_MS : DIRECT_DEBOUNCE_MS
  }

  return { search, warmup, mode, debounceMs }
}

if (globalThis.window?.__meilisearch) {
  globalThis.tcSearch = createSearchTransport({
    config: () => globalThis.window.__meilisearch,
  })
}
